import React, { useRef, useState, useCallback } from 'react';

/*
 * ScheduleDial — cadran solaire 24h pour choisir l'heure ON et l'heure OFF
 * d'un planning, en alternative aux menus deroulants de NewScheduleRow /
 * EditTimesRow.
 *
 * Recoit EXACTEMENT les memes props que NewScheduleRow/EditTimesRow pour
 * la partie horaire (onHourType/onHourValue/onMinuteOrOffset + setters,
 * idem off), afin de pouvoir reutiliser addSchedule/saveScheduleEdit tels
 * quels — aucun changement cote sauvegarde/API.
 *
 * sunrise/sunset : chaines "HH:MM" (meme format que sunTimes.sunrise/sunset
 * deja recupere par DeviceCard.js via get_sun_times.php).
 *
 * Pres du repere lever/coucher (+/- SUN_SNAP_MIN minutes), le point genere
 * un horaire de type "sun" (adaptatif jour apres jour) ; ailleurs, un
 * horaire "fixed" classique — comme le permet deja le backend.
 */

const SUN_SNAP_MIN = 90; // fenetre autour du lever/coucher qui reste "adaptative"
const TAU = Math.PI * 2;
const SIZE = 300;
const CX = SIZE / 2, CY = SIZE / 2;
const R = SIZE * 0.5 - 54;
const R_OUTER = R + R * 0.17;
const PAD = 34; // marge pour que les reperes lever/coucher (hors R_OUTER) ne soient pas rognes par le viewBox

function parseHM(hm) {
  if (!hm) return null;
  // get_sun_times.php renvoie "07h43" (separateur "h"), pas "07:43"
  const [h, m] = String(hm).replace('h', ':').split(':').map(Number);
  if (Number.isNaN(h) || Number.isNaN(m)) return null;
  return h + m / 60;
}

function fmtClock(h) {
  h = ((h % 24) + 24) % 24;
  const hh = Math.floor(h);
  let mm = Math.round((h - hh) * 60);
  let hh2 = hh, mm2 = mm;
  if (mm === 60) { hh2 = (hh + 1) % 24; mm2 = 0; }
  return `${String(hh2).padStart(2, '0')}:${String(mm2).padStart(2, '0')}`;
}

// Minuit en haut, midi en bas, lever a droite, coucher a gauche.
function hourToAngle(h) { return (h / 24) * TAU - Math.PI / 2; }
function angleToHour(a) {
  let h = ((a + Math.PI / 2) / TAU) * 24;
  return ((h % 24) + 24) % 24;
}
function pt(r, h) {
  const a = hourToAngle(h);
  return [CX + r * Math.cos(a), CY + r * Math.sin(a)];
}
function arcPath(r, hStart, hEnd) {
  let sweep = hEnd - hStart; if (sweep < 0) sweep += 24;
  const large = sweep > 12 ? 1 : 0;
  const [x0, y0] = pt(r, hStart);
  const [x1, y1] = pt(r, hEnd);
  return `M ${x0} ${y0} A ${r} ${r} 0 ${large} 1 ${x1} ${y1}`;
}
function snap5(h) { return Math.round(h * 12) / 12; }

// --- conversions entre "decimal hour" (utilise pour dessiner/glisser) et
// la representation type/valeur/offset attendue par le backend ---
function toDecimal(type, value, minuteOrOffset, sunriseDec, sunsetDec) {
  if (type === 'sun') {
    const base = value === 'sunrise' ? sunriseDec : sunsetDec;
    return base == null ? 0 : base + (Number(minuteOrOffset) || 0) / 60;
  }
  const hh = parseInt(value, 10) || 0;
  const mm = parseInt(minuteOrOffset, 10) || 0;
  return hh + mm / 60;
}
function fromDecimal(h, sunriseDec, sunsetDec) {
  h = ((h % 24) + 24) % 24;
  if (sunriseDec != null) {
    const d = Math.abs(((h - sunriseDec + 12 + 24) % 24) - 12) * 60;
    if (d <= SUN_SNAP_MIN) {
      return { type: 'sun', value: 'sunrise', minuteOrOffset: (Math.round((h - sunriseDec) * 12) / 12 * 60) | 0 };
    }
  }
  if (sunsetDec != null) {
    const d = Math.abs(((h - sunsetDec + 12 + 24) % 24) - 12) * 60;
    if (d <= SUN_SNAP_MIN) {
      return { type: 'sun', value: 'sunset', minuteOrOffset: (Math.round((h - sunsetDec) * 12) / 12 * 60) | 0 };
    }
  }
  const snapped = snap5(h);
  const hh = Math.floor(snapped);
  const mm = Math.round((snapped - hh) * 60);
  return { type: 'fixed', value: String(hh).padStart(2, '0'), minuteOrOffset: String(mm).padStart(2, '0') };
}

export default function ScheduleDial({
  sunrise, sunset,
  onHourType, onHourValue, onMinuteOrOffset, setOnHourType, setOnHourValue, setOnMinuteOrOffset,
  offHourType, offHourValue, offMinuteOrOffset, setOffHourType, setOffHourValue, setOffMinuteOrOffset,
}) {
  const svgRef = useRef(null);
  const [dragging, setDragging] = useState(null); // 'on' | 'off' | null

  const sunriseDec = parseHM(sunrise);
  const sunsetDec = parseHM(sunset);

  const onH = toDecimal(onHourType || 'fixed', onHourValue ?? '19', onMinuteOrOffset ?? '00', sunriseDec, sunsetDec);
  const offH = toDecimal(offHourType || 'fixed', offHourValue ?? '23', offMinuteOrOffset ?? '00', sunriseDec, sunsetDec);

  const applyOn = useCallback((h) => {
    const r = fromDecimal(h, sunriseDec, sunsetDec);
    setOnHourType(r.type); setOnHourValue(r.value); setOnMinuteOrOffset(r.minuteOrOffset);
  }, [sunriseDec, sunsetDec, setOnHourType, setOnHourValue, setOnMinuteOrOffset]);

  const applyOff = useCallback((h) => {
    const r = fromDecimal(h, sunriseDec, sunsetDec);
    setOffHourType(r.type); setOffHourValue(r.value); setOffMinuteOrOffset(r.minuteOrOffset);
  }, [sunriseDec, sunsetDec, setOffHourType, setOffHourValue, setOffMinuteOrOffset]);

  const angleFromEvent = useCallback((evt) => {
    const rect = svgRef.current.getBoundingClientRect();
    const scale = (SIZE + PAD * 2) / rect.width;
    const clientX = evt.touches ? evt.touches[0].clientX : evt.clientX;
    const clientY = evt.touches ? evt.touches[0].clientY : evt.clientY;
    const x = (clientX - rect.left) * scale - PAD - CX;
    const y = (clientY - rect.top) * scale - PAD - CY;
    return Math.atan2(y, x);
  }, []);

  const startDrag = (which) => (evt) => {
    evt.preventDefault();
    setDragging(which);
    const move = (e) => {
      const h = snap5(angleToHour(angleFromEvent(e)));
      if (which === 'on') applyOn(h); else applyOff(h);
    };
    const up = () => {
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', up);
      setDragging(null);
    };
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);
  };

  const clickSunset = () => { if (sunsetDec != null) applyOn(sunsetDec); };
  const clickSunrise = () => { if (sunriseDec != null) applyOff(sunriseDec); };

  const ticks = [];
  for (let q = 0; q < 96; q++) {
    const h = q / 4;
    const isHour = q % 4 === 0;
    const isMajor = [0, 6, 12, 18].includes(h);
    const rInner = R_OUTER - (isMajor ? 12 : isHour ? 8 : 4);
    const [x0, y0] = pt(rInner, h);
    const [x1, y1] = pt(R_OUTER, h);
    ticks.push(
      <line key={q} x1={x0} y1={y0} x2={x1} y2={y1}
        stroke={isMajor ? '#8f8874' : '#d6d0bf'} strokeWidth={isMajor ? 2 : isHour ? 1.2 : 1}
        strokeLinecap="round" />
    );
    if (isMajor) {
      const [lx, ly] = pt(R_OUTER + 14, h);
      ticks.push(
        <text key={'l' + q} x={lx} y={ly + 4} textAnchor="middle" fontSize="11"
          fontWeight={600} fill="#8f8874">{String(h).padStart(2, '0')}h</text>
      );
    }
  }

  function SunMark({ hourValue, icon, word, onClick }) {
    if (hourValue == null) return null;
    const [px, py] = pt(R_OUTER + 28, hourValue);
    return (
      <g style={{ cursor: 'pointer' }} onClick={onClick} tabIndex={0} role="button"
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onClick(); } }}>
        <rect x={px - 28} y={py - 13} width={56} height={44} fill="transparent" />
        <text x={px} y={py} textAnchor="middle" fontSize="14">{icon}</text>
        <text x={px} y={py + 12} textAnchor="middle" fontSize="9" fontWeight={700}
          letterSpacing=".03em" fill="#7a7566">{word}</text>
        <text x={px} y={py + 25} textAnchor="middle" fontSize="10.5" fontWeight={600}
          fill="#1d1a12">{fmtClock(hourValue)}</text>
      </g>
    );
  }

  function Handle({ hourValue, color, label, which }) {
    const [x, y] = pt(R, hourValue);
    return (
      <g transform={`translate(${x} ${y})`} style={{ cursor: dragging === which ? 'grabbing' : 'grab' }}
        onPointerDown={startDrag(which)}>
        <circle r={19} fill="transparent" />
        <circle r={13} fill={color} stroke="#fff" strokeWidth={3.5} />
        <text textAnchor="middle" dy={3.5} fontSize="9.5" fontWeight={700} fill="#fff">{label}</text>
      </g>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8, touchAction: 'none', userSelect: 'none' }}>
      <svg ref={svgRef} viewBox={`${-PAD} ${-PAD} ${SIZE + PAD * 2} ${SIZE + PAD * 2}`} style={{ width: '100%', maxWidth: 300 + PAD * 2 }}>
        {sunriseDec != null && sunsetDec != null && (
          <>
            <path d={arcPath(R, sunriseDec, sunsetDec)} fill="none" stroke="#fbf3dd" strokeWidth={R * 0.30} />
            <path d={arcPath(R, sunsetDec, sunriseDec)} fill="none" stroke="#5c6470" strokeWidth={R * 0.30} />
          </>
        )}
        <path d={arcPath(R, onH, offH)} fill="none" stroke="#0b8a4f" strokeWidth={R * 0.32} strokeLinecap="round" />
        {ticks}
        <SunMark hourValue={sunriseDec} icon="☀" word="lever" onClick={clickSunrise} />
        <SunMark hourValue={sunsetDec} icon="☾" word="coucher" onClick={clickSunset} />
        <text x={CX} y={CY - 28} textAnchor="middle" fontSize="9.5" fontWeight={700}
          letterSpacing=".08em" fill="#7a7566">DÉBUT</text>
        <text x={CX} y={CY - 7} textAnchor="middle" fontSize="20" fontWeight={700} fill="#0b8a4f">{fmtClock(onH)}</text>
        <text x={CX} y={CY + 22} textAnchor="middle" fontSize="9.5" fontWeight={700}
          letterSpacing=".08em" fill="#7a7566">FIN</text>
        <text x={CX} y={CY + 43} textAnchor="middle" fontSize="20" fontWeight={700} fill="#4f6480">{fmtClock(offH)}</text>
        <Handle hourValue={onH} color="#0b8a4f" label="ON" which="on" />
        <Handle hourValue={offH} color="#4f6480" label="OFF" which="off" />
      </svg>
    </div>
  );
}
